import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PharmacyPageHeader, LoadingBlock, formatNumber } from '../ui';
import StockTable from '../components/StockTable';
import MedicationSmartPicker from '../components/MedicationSmartPicker';
import Icon from '../../common/Icons';
import { useInventoryRoutes } from '../components/inventoryUi';
import { PHARMACY_COPY } from '../pharmacyCopy';

const invCopy = PHARMACY_COPY.INVENTORY;

export default function PharmacyDutyCabinetPage() {
  const { centralSite, toast, bumpReload, reloadNonce } = usePharmacy();
  const routes = useInventoryRoutes();
  const [enabled, setEnabled] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [mainStock, setMainStock] = useState([]);
  const [lines, setLines] = useState([{ medication_id: '', quantity: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const siteId = centralSite?.id;

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError('');
    try {
      const res = await pharmacyService.getDutyCabinetStock({ site_inventory_only: 'false' });
      if (res.success) {
        setEnabled(true);
      } else {
        setEnabled(false);
        setError(res.message || 'Armoire de garde indisponible.');
      }
    } catch (e) {
      setEnabled(false);
      setError(e.message || 'Armoire de garde indisponible.');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta, reloadNonce]);

  useEffect(() => {
    if (!siteId || !enabled) {
      setMainStock([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await pharmacyService.getSiteStock(siteId, {
          in_stock_only: 'true',
          stock_location: 'MAIN'
        });
        if (!cancelled) {
          setMainStock(res.success && Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setMainStock([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId, enabled, reloadNonce]);

  const medOptions = useMemo(
    () =>
      mainStock.map((row) => ({
        id: row.medication_id,
        name: row.medication_name,
        unit_name: row.unit_name,
        on_hand: row.on_hand
      })),
    [mainStock]
  );

  const updateLine = (index, patch) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine = () => setLines((prev) => [...prev, { medication_id: '', quantity: '' }]);

  const removeLine = (index) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleRestock = async (event) => {
    event.preventDefault();
    const items = lines
      .map((line) => ({
        medication_id: parseInt(line.medication_id, 10),
        quantity: parseInt(line.quantity, 10)
      }))
      .filter((line) => line.medication_id > 0 && line.quantity > 0);

    if (!items.length) {
      toast?.('warning', 'Ajoutez au moins un médicament et une quantité.');
      return;
    }

    setBusy(true);
    try {
      const res = await pharmacyService.restockDutyCabinet({ items });
      if (!res.success) {
        toast?.('danger', res.message || 'Réassort impossible.');
        return;
      }
      toast?.('success', 'Armoire de garde réapprovisionnée.');
      setLines([{ medication_id: '', quantity: '' }]);
      bumpReload?.();
      loadMeta();
    } catch (e) {
      toast?.('danger', e.message || 'Réassort impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (loadingMeta) {
    return <LoadingBlock label="Chargement de l’armoire de garde…" />;
  }

  if (!centralSite) {
    return (
      <div className="cp-empty-state cp-card">
        <h3 className="cp-empty-state__title">Aucune pharmacie centrale configurée</h3>
      </div>
    );
  }

  if (enabled === false) {
    return (
      <div className="cp-stock-page">
        <PharmacyPageHeader
          badge="Armoire de garde"
          title="Armoire de garde"
          description="Stock séparé pour la nurse / le major sur le site central."
        />
        <Alert variant="warning" className="mb-0">
          {error ||
            'L’armoire de garde n’est pas activée. Activez-la sur le site pharmacie centrale (Administration → Sites).'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="cp-stock-page">
      <PharmacyPageHeader
        badge="Armoire de garde"
        title="Armoire de garde"
        description={`Stock DUTY sur ${centralSite.name} — réassort depuis le stock principal (MAIN), délivrance nurse / major uniquement.`}
        actions={
            <Link to={routes.LIST} className="cp-btn cp-btn--ghost">
            <Icon name="clipboard-list" size={16} aria-hidden />
            {invCopy.NEW_BUTTON}
          </Link>
        }
      />

      <section className="cp-card mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <Icon name="inbox" size={18} aria-hidden />
          <h2 className="h5 mb-0">Réassort depuis le stock principal</h2>
        </div>
        <Form onSubmit={handleRestock}>
          <Table responsive size="sm" className="align-middle mb-3">
            <thead>
              <tr>
                <th>Médicament</th>
                <th style={{ width: '8rem' }}>Qté</th>
                <th style={{ width: '3rem' }} aria-label="Retirer" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={`duty-line-${index}`}>
                  <td>
                    <MedicationSmartPicker
                      value={line.medication_id}
                      medications={medOptions}
                      onChange={(id) => updateLine(index, { medication_id: id })}
                    />
                    {line.medication_id ? (
                      <div className="small text-muted mt-1">
                        Dispo MAIN :{' '}
                        {formatNumber(
                          medOptions.find((m) => String(m.id) === String(line.medication_id))?.on_hand || 0
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min={1}
                      size="sm"
                      value={line.quantity}
                      onChange={(e) => updateLine(index, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => removeLine(index)}
                      disabled={lines.length <= 1}
                      aria-label="Retirer la ligne"
                    >
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-secondary" size="sm" onClick={addLine}>
              Ajouter une ligne
            </Button>
            <Button type="submit" variant="success" size="sm" disabled={busy}>
              {busy ? 'Réassort…' : 'Réapprovisionner l’armoire'}
            </Button>
          </div>
        </Form>
      </section>

      <section>
        <h2 className="h5 mb-3">Stock actuel de l’armoire</h2>
        <StockTable
          key={`duty-stock-${siteId}-${reloadNonce}`}
          siteId={siteId}
          allowAdjust={false}
          siteInventoryOnly
          stockLocation="DUTY"
          siteLabel={`Armoire de garde · ${centralSite.name}`}
        />
      </section>
    </div>
  );
}
